import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

import config from '../../config.js';
import '../../styles/status.css';
import '../../styles/pagination.css';

import PageHeader from '../../components/general/pageHeader.js';
import PageTable from '../../components/general/pageTable';
import useAuthenticate from '../../components/authentication/authenticate';
import Loading from '../../components/general/loading.js';
import AmenityBooking from '../../components/amenities/AmenityBooking.js';
import AmenitySettings from '../../components/amenities/AmenitySettings.js';
import AmenityCards from '../../components/cards/amenityCard.js';

import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';

const MAmenities = () => {
  const { isLoading } = useAuthenticate();
  const queryClient = useQueryClient();

  const [selectedProperty, setSelectedProperty] = useState(null);
  const [view, setView] = useState('table');
  const [page, setPage] = useState(1);
  const rowsPerPage = 10;

  const { data: properties = [], isLoading: isPropertiesLoading} = useQuery({
    queryKey: ['properties'],
    queryFn: fetchProperties,
  });

  // useEffect(() => {
  //   console.log('Selected Property changed:', selectedProperty);
  //   }
  // , [selectedProperty]);

  const { data: bookingData = {}, isFetching: isBookingsLoading } = useQuery({
    queryKey: ['bookings', selectedProperty?.value, page],
    queryFn: async () => {
      if (!selectedProperty?.value) return { results: [], totalPages: 1, totalResults: 0 };
      return fetchBookingsByProperty(selectedProperty.value, page, rowsPerPage);
    },
    enabled: !!selectedProperty?.value,
    keepPreviousData: true,
  });

  const isTableLoading =  isPropertiesLoading || isBookingsLoading;

  // useEffect(() => {
  //   if (selectedProperty?.value) {
  //     queryClient.invalidateQueries(['bookings']); // ✅ refetch when property changes
  //   }
  // }, [selectedProperty, queryClient]);

  const bookings = bookingData.results || [];
  const totalPages = bookingData.totalPages || 1;

  // useEffect(() => {
  //   if (properties?.length > 0 && !selectedProperty) {
  //     setSelectedProperty(properties[0]);
  //   }
  // }, [properties, selectedProperty]);

  const handleBookingComplete = () => {
    queryClient.invalidateQueries({ queryKey: ['bookings'] });
    setView('table');
  };

  if (view === 'booking') {
    return (
      <AmenityBooking
        onBack={() => setView('table')}
        onBookingComplete={handleBookingComplete}
      />
    );
  } else if (view === 'settings') {
    return <AmenitySettings onBack={() => setView('table')} />;
  }

  // ✅ Loading screen (matches GatePass style)
  if (isLoading || isTableLoading) {
    return (
      <div className="w-full h-[75vh] flex flex-col">
        <PageHeader pageName="Amenities" />
        <div className="flex flex-1 justify-center items-center">
          <Loading />
        </div>
      </div>
    );
  }

  const maxVisibleButtons = 10;
  const startPage = Math.floor((page - 1) / maxVisibleButtons) * maxVisibleButtons + 1;
  const endPage = Math.min(startPage + maxVisibleButtons - 1, totalPages);

  return (
    <div className="w-full full-page">
      <div className="page-content">
        <PageHeader pageName="Amenities" />

        {/* Header Section */}
        <div className="page-header flex flex-col md:flex-row gap-3 items-center justify-between">
          <div className="w-full md:w-[350px]">
            <Autocomplete
              options={properties}
              value={selectedProperty}
              onChange={(e, newVal) => {
                if (newVal && newVal.value && newVal.label) {
                  setSelectedProperty(newVal);
                  setPage(1);
                } else {
                  setSelectedProperty(null);
                }
              }}
              isOptionEqualToValue={(option, value) =>
                option?.value === value?.value
              }
              getOptionLabel={(option) =>
                typeof option === 'string'
                  ? option
                  : option?.label || option?.name || ''
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Property"
                  variant="outlined"
                  fullWidth
                />
              )}
            />
          </div>

          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', height: '100%' }}>
            <button
              className="header-button"
              onClick={() => setView('settings')}
              style={{ height: '100%' }}
            >
              <i className="fas fa-gear"></i> Amenity Settings
            </button>
            <button
              className="header-button"
              onClick={() => setView('booking')}
              style={{ height: '100%' }}
            >
              <i className="fas fa-water-ladder"></i> Book Amenity
            </button>
          </div>
        </div>

        <>
          {/* Mobile Cards */}
          <div className="block md:hidden mt-4">
            <AmenityCards rows={bookings} />
          </div>

          {/* Desktop Table */}
          <div className="hidden md:block table-wrapper">
            <PageTable
              rows={bookings}
              columns={[
                {
                  label: 'Amenity',
                  accessor: (row) => row.amenity?.name || 'N/A',
                  width: '20%',
                },
                {
                  label: 'Date',
                  accessor: (row) =>
                    new Date(row.bookingDate).toLocaleDateString(),
                  width: '15%',
                },
                {
                  label: 'Booked by',
                  accessor: (row) => row.bookedBy?.user?.givenName + ' ' + row.bookedBy?.user?.lastName || 'N/A',
                  width: '15%',
                },
                {
                  label: 'From',
                  accessor: (row) => `${row.startTime}`,
                  width: '10%',
                },
                {
                  label: 'To',
                  accessor: (row) => `${row.endTime}`,
                  width: '10%',
                },
                {
                  label: 'Status',
                  accessor: (row) => (
                    <span
                      className={`amenity-status-badge ${row.status?.toLowerCase()}`}
                    >
                      {row.status}
                    </span>
                  ),
                  width: '15%',
                },
                {
                  label: 'Notes',
                  accessor: (row) => row.notes || '-',
                  width: '10%',
                },
              ]}
              hasButtons={false}
            />
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="pagination-controls mt-4">
              <div className="pagination-buttons">
                <button
                  className="pagination-button"
                  onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                  disabled={page === 1}
                >
                  <i className="fas fa-chevron-left"></i>
                </button>

                {startPage > 1 && (
                  <button className="pagination-page" onClick={() => setPage(startPage - 1)}>
                    ...
                  </button>
                )}

                {Array.from({ length: endPage - startPage + 1 }, (_, i) => {
                  const pageNum = startPage + i;
                  return (
                    <button
                      key={pageNum}
                      className={`pagination-page ${page === pageNum ? 'active' : ''}`}
                      onClick={() => setPage(pageNum)}
                    >
                      {pageNum}
                    </button>
                  );
                })}

                {endPage < totalPages && (
                  <button className="pagination-page" onClick={() => setPage(endPage + 1)}>
                    ...
                  </button>
                )}

                <button
                  className="pagination-button"
                  onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
                  disabled={page === totalPages}
                >
                  <i className="fas fa-chevron-right"></i>
                </button>
              </div>
            </div>
          )}
        </>
      </div>
    </div>
  );
};

const fetchProperties = async () => {
  const response = await axios.get(`${config.API_URL}/v2/property`, {
    headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
  });
  return response.data.map((item) => ({
    value: item._id,
    label: item.name,
  }));
};

const fetchBookingsByProperty = async (propertyId, page = 1, limit = 10) => {
  if (!propertyId) {
    console.warn('⛔ No propertyId provided — skipping fetch');
    return { results: [], totalPages: 1, totalResults: 0 };
  }


  try {
    const response = await axios.get(`${config.API_URL}/v2/amenity/booking`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
      params: { propertyId, page, limit, sortBy: 'createdAt', sortOrder: 'desc' },
    });
    return response.data;
  } catch (error) {
    console.error('❌ Error fetching paginated bookings by property:', error);
    return { results: [], totalPages: 1, totalResults: 0 };
  }
};


export default MAmenities;
